/**
 * Main hook for warehouse table functionality
 * Combines all table operations in a single, easy-to-use hook
 */

import { useState, useEffect, useMemo, useCallback } from 'react';
import type {
  TableDataRow,
  ColumnDefinition,
  TableConfig,
  TableState,
  SortDirection,
  FilterState,
  SummaryCalculation,
  RowSelectionState,
  RowSelectionConfig,
  ExpandableRowConfig,
  ServerPaginationConfig,
  ServerFetchParams
} from '../types/table';
import { 
  sortData, 
  filterData, 
  paginateData, 
  calculateSummary, 
  exportToCSV,
  saveTableConfig,
  loadTableConfig 
} from '../utils/tableUtils';

interface UseWarehouseTableProps<T extends TableDataRow> {
  data: T[];
  columns: ColumnDefinition<T>[];
  config?: Partial<TableConfig>;
  summaryCalculations?: SummaryCalculation<T>[];

  // Row Selection (Hybrid approach)
  selectedRows?: (string | number)[]; // External control
  onSelectionChange?: (selectedRows: (string | number)[], selectedData: T[]) => void;
  getRowId?: (row: T) => string | number; // Custom ID extraction

  // Expandable rows (optional)
  expandableConfig?: ExpandableRowConfig<T, any>;

  // Server-side pagination (optional — when enabled, table delegates pagination/sort/filter to server)
  serverPagination?: ServerPaginationConfig<T>;
}

interface UseWarehouseTableReturn<T extends TableDataRow> {
  // State
  tableState: TableState<T>;

  // Actions
  handleSort: (column: keyof T) => void;
  handleFilter: (filters: Partial<FilterState>) => void;
  handlePageChange: (page: number) => void;
  handlePageSizeChange: (pageSize: number) => void;
  handleColumnToggle: (columnId: string) => void;
  handleColumnReorder: (fromIndex: number, toIndex: number) => void;
  handleColumnResize: (columnId: string, width: number) => void;
  handleExport: () => void;
  handleReset: () => void;

  // Row Selection Actions
  handleRowSelect: (rowId: string | number, selected: boolean) => void;
  handleSelectAll: (selected: boolean) => void;
  handleSelectionClear: () => void;

  // Expandable Row Actions (optional - only present when expandableConfig is provided)
  expandedRows?: Set<string | number>;
  handleToggleExpand?: (rowId: string | number) => void;
  handleExpandAll?: () => void;
  handleCollapseAll?: () => void;

  // Summary data
  summaryData: Record<string, number>;

  // Configuration
  visibleColumns: ColumnDefinition<T>[];
}

const DEFAULT_CONFIG: TableConfig = {
  showPagination: true,
  showColumnConfig: true,
  showGlobalSearch: true,
  showFilters: true,
  showExport: false,
  showSummary: false,
  defaultPageSize: 50,
  allowColumnReorder: true,
  allowColumnResize: true,
  allowRowSelection: false,
  stickyHeader: true,
  stickyFooter: false,
  compactMode: true
};

export const useWarehouseTable = <T extends TableDataRow>({
  data,
  columns: initialColumns,
  config: userConfig = {},
  summaryCalculations = [],
  selectedRows: externalSelectedRows,
  onSelectionChange,
  getRowId,
  expandableConfig,
  serverPagination
}: UseWarehouseTableProps<T>): UseWarehouseTableReturn<T> => {

  const isServerMode = serverPagination?.enabled ?? false;
  
  const config = { ...DEFAULT_CONFIG, ...userConfig };
  
  // Core state
  const [columns, setColumns] = useState<ColumnDefinition<T>[]>(initialColumns);
  const [sortColumn, setSortColumn] = useState<keyof T | null>(null);
  const [sortDirection, setSortDirection] = useState<SortDirection>(null);
  const [filters, setFilters] = useState<FilterState>({
    globalSearch: '',
    columnFilters: {},
    dropdownFilters: {}
  });
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(config.defaultPageSize || 50);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string>();

  // Default row ID function
  const defaultGetRowId = useCallback((row: T): string | number => {
    return row.id || (row as any).itemNo || JSON.stringify(row);
  }, []);

  const effectiveGetRowId = getRowId || defaultGetRowId;

  // Row selection configuration
  const selectionConfig: RowSelectionConfig = useMemo(() => {
    const allowSelection = config.allowRowSelection;
    if (typeof allowSelection === 'object') {
      return allowSelection;
    }
    return {
      mode: 'multi',
      selectAllMode: 'all-data',
      getRowId: effectiveGetRowId
    };
  }, [config.allowRowSelection, effectiveGetRowId]);

  // Row selection state (internal or external)
  const isControlledSelection = externalSelectedRows !== undefined;
  const [internalSelectedRows, setInternalSelectedRows] = useState<Set<string | number>>(new Set());

  // Expandable rows state (only used when expandableConfig is provided)
  const [expandedRows, setExpandedRows] = useState<Set<string | number>>(
    new Set(expandableConfig?.defaultExpanded || [])
  );
  
  const selectedRowsSet = useMemo(() => {
    if (isControlledSelection) {
      return new Set(externalSelectedRows);
    }
    return internalSelectedRows;
  }, [isControlledSelection, externalSelectedRows, internalSelectedRows]);

  // Load saved configuration on mount
  useEffect(() => {
    if (config.persistenceKey) {
      const savedConfig = loadTableConfig(config.persistenceKey);
      if (savedConfig) {
        // Apply saved column configuration
        const updatedColumns = columns.map(col => {
          const savedCol = savedConfig.columns.find((c: any) => c.id === col.id);
          if (savedCol) {
            return {
              ...col,
              visible: savedCol.visible,
              width: savedCol.width
            };
          }
          return col;
        });
        
        // Reorder columns based on saved order
        const orderedColumns = savedConfig.columns
          .sort((a: any, b: any) => a.order - b.order)
          .map((savedCol: any) => updatedColumns.find(col => col.id === savedCol.id))
          .filter(Boolean);
        
        // Add any new columns that weren't in saved config
        const newColumns = updatedColumns.filter(col => 
          !savedConfig.columns.some((c: any) => c.id === col.id)
        );
        
        setColumns([...orderedColumns, ...newColumns] as ColumnDefinition<T>[]);
        
        if (savedConfig.pageSize) {
          setPageSize(savedConfig.pageSize);
        }
      }
    }
  }, [config.persistenceKey]);

  // Sync columns when initialColumns prop changes (e.g., editOptions populated after master data loads)
  useEffect(() => {
    setColumns(prev => {
      // Update existing columns while preserving user customizations (visibility, width, order)
      const updatedPrev = prev.map(col => {
        const updated = initialColumns.find(c => c.id === col.id);
        if (updated) {
          return {
            ...updated,           // New column data (editOptions, formatter, etc.)
            visible: col.visible, // Keep user's visibility choice
            width: col.width      // Keep user's resize choice
          };
        }
        return col;
      });

      // Add any new columns from initialColumns that don't exist yet
      const existingIds = new Set(prev.map(c => c.id));
      const newCols = initialColumns.filter(c => !existingIds.has(c.id));
      if (newCols.length > 0) {
        return [...updatedPrev, ...newCols];
      }

      return updatedPrev;
    });
  }, [initialColumns]);

  // Visible columns
  const visibleColumns = useMemo(() => 
    columns.filter(col => col.visible), 
    [columns]
  );

  // Apply sorting and filtering (skipped in server mode — server handles this)
  const processedData = useMemo(() => {
    if (isServerMode) {
      // Server mode: data is already filtered/sorted by the server
      return data;
    }

    let result = [...data];

    // Apply filtering
    result = filterData(
      result,
      visibleColumns,
      filters.columnFilters || {},
      filters.dropdownFilters || {},
      filters.globalSearch
    );

    // Apply sorting
    result = sortData(result, sortColumn, sortDirection);

    return result;
  }, [data, visibleColumns, filters, sortColumn, sortDirection, isServerMode]);

  // Apply pagination (skipped in server mode — server handles this)
  const { paginatedData, totalPages } = useMemo(() => {
    if (isServerMode) {
      // Server mode: data is already the correct page, calculate totalPages from serverPagination.totalRecords
      const serverTotalPages = Math.max(1, Math.ceil((serverPagination?.totalRecords || 0) / pageSize));
      return { paginatedData: data, totalPages: serverTotalPages };
    }
    return paginateData(processedData, currentPage, pageSize);
  }, [processedData, currentPage, pageSize, isServerMode, data, serverPagination?.totalRecords]);

  // Reset to first page when data changes
  useEffect(() => {
    if (currentPage > totalPages && totalPages > 0) {
      setCurrentPage(1);
    }
  }, [totalPages, currentPage]);

  // Calculate summaries
  const summaryData = useMemo(() => {
    const summaries: Record<string, number> = {};
    
    summaryCalculations.forEach(calc => {
      const columnKey = String(calc.column);
      if (calc.type === 'custom' && calc.customCalculation) {
        summaries[columnKey] = calc.customCalculation(processedData);
      } else {
        summaries[columnKey] = calculateSummary(processedData, calc.column, calc.type as 'sum' | 'average' | 'count' | 'min' | 'max');
      }
    });
    
    return summaries;
  }, [processedData, summaryCalculations]);

  // Save configuration when columns or pageSize change
  useEffect(() => {
    if (config.persistenceKey) {
      saveTableConfig(config.persistenceKey, columns, pageSize);
    }
  }, [columns, pageSize, config.persistenceKey]);

  // Helper to build server fetch params from current state
  const buildServerFetchParams = useCallback((
    overrides: {
      page?: number;
      size?: number;
      sortCol?: keyof T | null;
      sortDir?: SortDirection;
      filterState?: FilterState;
    } = {}
  ): ServerFetchParams => {
    const page = overrides.page ?? currentPage;
    const size = overrides.size ?? pageSize;
    const col = overrides.sortCol !== undefined ? overrides.sortCol : sortColumn;
    const dir = overrides.sortDir !== undefined ? overrides.sortDir : sortDirection;
    const f = overrides.filterState ?? filters;

    return {
      pageNumber: page,
      pageSize: size,
      sortColumn: col ? String(col) : undefined,
      sortDirection: dir || undefined,
      filters: f.columnFilters,
      dropdownFilters: f.dropdownFilters,
      globalSearch: f.globalSearch
    };
  }, [currentPage, pageSize, sortColumn, sortDirection, filters]);

  // Action handlers
  const handleSort = useCallback((column: keyof T) => {
    let newSortColumn: keyof T | null = column;
    let newSortDirection: SortDirection;

    if (sortColumn === column) {
      // Cycle through: asc -> desc -> none
      if (sortDirection === 'asc') {
        newSortDirection = 'desc';
      } else if (sortDirection === 'desc') {
        newSortDirection = null;
        newSortColumn = null;
      } else {
        newSortDirection = 'asc';
      }
    } else {
      newSortDirection = 'asc';
    }

    setSortColumn(newSortColumn);
    setSortDirection(newSortDirection);
    setCurrentPage(1); // Reset to first page on sort

    if (isServerMode && serverPagination) {
      serverPagination.onFetchData(buildServerFetchParams({
        page: 1,
        sortCol: newSortColumn,
        sortDir: newSortDirection
      }));
    }
  }, [sortColumn, sortDirection, isServerMode, serverPagination, buildServerFetchParams]);

  const handleFilter = useCallback((newFilters: Partial<FilterState>) => {
    const updatedFilters = { ...filters, ...newFilters };
    setFilters(updatedFilters);
    setCurrentPage(1); // Reset to first page on filter

    if (isServerMode && serverPagination) {
      serverPagination.onFetchData(buildServerFetchParams({
        page: 1,
        filterState: updatedFilters
      }));
    }
  }, [filters, isServerMode, serverPagination, buildServerFetchParams]);

  const handlePageChange = useCallback((page: number) => {
    setCurrentPage(page);

    if (isServerMode && serverPagination) {
      serverPagination.onFetchData(buildServerFetchParams({ page }));
    }
  }, [isServerMode, serverPagination, buildServerFetchParams]);

  const handlePageSizeChange = useCallback((newPageSize: number) => {
    setPageSize(newPageSize);
    setCurrentPage(1);

    if (isServerMode && serverPagination) {
      serverPagination.onFetchData(buildServerFetchParams({ page: 1, size: newPageSize }));
    }
  }, [isServerMode, serverPagination, buildServerFetchParams]);

  const handleColumnToggle = useCallback((columnId: string) => {
    setColumns(prev => prev.map(col => 
      col.id === columnId ? { ...col, visible: !col.visible } : col
    ));
  }, []);

  const handleColumnReorder = useCallback((fromIndex: number, toIndex: number) => {
    setColumns(prev => {
      const newColumns = [...prev];
      const [movedColumn] = newColumns.splice(fromIndex, 1);
      newColumns.splice(toIndex, 0, movedColumn);
      return newColumns;
    });
  }, []);

  const handleColumnResize = useCallback((columnId: string, width: number) => {
    setColumns(prev => prev.map(col => 
      col.id === columnId ? { ...col, width: Math.max(col.minWidth || 60, width) } : col
    ));
  }, []);

  const handleExport = useCallback(() => {
    const filename = `${config.persistenceKey || 'warehouse-data'}-${new Date().toISOString().split('T')[0]}.csv`;
    exportToCSV(processedData, visibleColumns, filename);
  }, [processedData, visibleColumns, config.persistenceKey]);

  const handleReset = useCallback(() => {
    setColumns(initialColumns);
    setSortColumn(null);
    setSortDirection(null);
    setFilters({
      globalSearch: '',
      columnFilters: {},
      dropdownFilters: {}
    });
    setCurrentPage(1);
    setPageSize(config.defaultPageSize || 50);
    
    // Clear saved configuration
    if (config.persistenceKey) {
      localStorage.removeItem(`warehouse-table-${config.persistenceKey}`);
    }
  }, [initialColumns, config.persistenceKey, config.defaultPageSize]);

  // Row Selection Handlers
  const handleRowSelect = useCallback((rowId: string | number, selected: boolean) => {
    let newSelectedRows: Set<string | number>;
    
    if (selectionConfig.mode === 'single') {
      newSelectedRows = selected ? new Set([rowId]) : new Set();
    } else {
      newSelectedRows = new Set(selectedRowsSet);
      if (selected) {
        newSelectedRows.add(rowId);
      } else {
        newSelectedRows.delete(rowId);
      }
    }

    // Update internal state if uncontrolled
    if (!isControlledSelection) {
      setInternalSelectedRows(newSelectedRows);
    }

    // Notify parent of change
    if (onSelectionChange) {
      const selectedRowsArray = Array.from(newSelectedRows);
      const selectedData = data.filter(row => newSelectedRows.has(effectiveGetRowId(row)));
      onSelectionChange(selectedRowsArray, selectedData);
    }
  }, [selectedRowsSet, selectionConfig.mode, isControlledSelection, onSelectionChange, data, effectiveGetRowId]);

  const handleSelectAll = useCallback((selected: boolean) => {
    let targetData: T[];
    
    if (selectionConfig.selectAllMode === 'all-data') {
      targetData = processedData; // All filtered data
    } else {
      targetData = paginatedData; // Current page only
    }

    const newSelectedRows = new Set(selectedRowsSet);
    
    if (selected) {
      targetData.forEach(row => {
        newSelectedRows.add(effectiveGetRowId(row));
      });
    } else {
      targetData.forEach(row => {
        newSelectedRows.delete(effectiveGetRowId(row));
      });
    }

    // Update internal state if uncontrolled
    if (!isControlledSelection) {
      setInternalSelectedRows(newSelectedRows);
    }

    // Notify parent of change
    if (onSelectionChange) {
      const selectedRowsArray = Array.from(newSelectedRows);
      const selectedData = data.filter(row => newSelectedRows.has(effectiveGetRowId(row)));
      onSelectionChange(selectedRowsArray, selectedData);
    }
  }, [selectedRowsSet, selectionConfig.selectAllMode, processedData, paginatedData, isControlledSelection, onSelectionChange, data, effectiveGetRowId]);

  const handleSelectionClear = useCallback(() => {
    const newSelectedRows = new Set<string | number>();

    // Update internal state if uncontrolled
    if (!isControlledSelection) {
      setInternalSelectedRows(newSelectedRows);
    }

    // Notify parent of change
    if (onSelectionChange) {
      onSelectionChange([], []);
    }
  }, [isControlledSelection, onSelectionChange]);

  // Expandable Row Handlers (only used when expandableConfig is provided)
  const handleToggleExpand = useCallback((rowId: string | number) => {
    setExpandedRows(prev => {
      const newSet = new Set(prev);
      const isExpanding = !newSet.has(rowId);

      if (isExpanding) {
        newSet.add(rowId);
        // Call onExpand callback when expanding a row (useful for lazy loading)
        if (expandableConfig?.onExpand) {
          const row = data.find(r => effectiveGetRowId(r) === rowId);
          if (row) {
            expandableConfig.onExpand(row);
          }
        }
      } else {
        newSet.delete(rowId);
      }

      expandableConfig?.onExpandChange?.(Array.from(newSet));
      return newSet;
    });
  }, [expandableConfig, data, effectiveGetRowId]);

  const handleExpandAll = useCallback(() => {
    const allIds = processedData.map(row => effectiveGetRowId(row));
    const newSet = new Set(allIds);
    setExpandedRows(newSet);
    expandableConfig?.onExpandChange?.(allIds);
  }, [processedData, effectiveGetRowId, expandableConfig]);

  const handleCollapseAll = useCallback(() => {
    setExpandedRows(new Set());
    expandableConfig?.onExpandChange?.([]);
  }, [expandableConfig]);

  // Calculate selection state for UI
  const selectedData = useMemo(() => {
    return data.filter(row => selectedRowsSet.has(effectiveGetRowId(row)));
  }, [data, selectedRowsSet, effectiveGetRowId]);

  const allCurrentPageSelected = useMemo(() => {
    return paginatedData.length > 0 && paginatedData.every(row => selectedRowsSet.has(effectiveGetRowId(row)));
  }, [paginatedData, selectedRowsSet, effectiveGetRowId]);

  const someCurrentPageSelected = useMemo(() => {
    return paginatedData.some(row => selectedRowsSet.has(effectiveGetRowId(row)));
  }, [paginatedData, selectedRowsSet, effectiveGetRowId]);

  const selectionState: RowSelectionState<T> = {
    selectedRows: selectedRowsSet,
    isSelectAllChecked: allCurrentPageSelected,
    isSelectAllIndeterminate: someCurrentPageSelected && !allCurrentPageSelected,
    selectedData
  };

  // Table state
  const tableState: TableState<T> = {
    data,
    filteredData: processedData,
    paginatedData,
    columns,
    visibleColumns,
    filters,
    sort: { column: sortColumn, direction: sortDirection },
    pagination: {
      currentPage,
      pageSize,
      totalRows: isServerMode ? (serverPagination?.totalRecords || 0) : processedData.length,
      totalPages
    },
    selection: selectionState,
    loading,
    error
  };

  return {
    tableState,
    handleSort,
    handleFilter,
    handlePageChange,
    handlePageSizeChange,
    handleColumnToggle,
    handleColumnReorder,
    handleColumnResize,
    handleExport,
    handleReset,
    handleRowSelect,
    handleSelectAll,
    handleSelectionClear,
    summaryData,
    visibleColumns,
    // Expandable row state and handlers (only when expandableConfig is provided)
    ...(expandableConfig && {
      expandedRows,
      handleToggleExpand,
      handleExpandAll,
      handleCollapseAll
    })
  };
};